import React from "react";
import styled, { css } from "styled-components";
import { fallbackValues } from "./FormLayouts.theme.js";
import { themeComponent } from "../../../util/themeUtils";
import { createIdFromString } from "../../../util/general.js";
import Text from "../text";
import { Box, Cluster, Stack } from "../layouts";
import { FONT_WEIGHT_REGULAR } from "../../../constants/style_constants";
import { ERROR_COLOR, ROYAL_BLUE } from "../../../constants/colors";

const TextareaField = styled.textarea`
  border: 1px solid
    ${({ field, showErrors, themeValues }) =>
      (field.dirty && field.hasErrors) || (field.hasErrors && showErrors)
        ? ERROR_COLOR
        : themeValues.borderColor};
  border-radius: 2px;
  height: ${({ $customHeight }) => ($customHeight ? $customHeight : "auto")};
  width: 100%;
  padding: 1rem;
  min-width: 100px;
  margin: 0;
  box-sizing: border-box;
  position: relative;
  font-size: 1.1rem;
  font-family: Public Sans;
  line-height: 1.5rem;
  font-weight: ${FONT_WEIGHT_REGULAR};
  background-color: ${({ themeValues }) =>
    themeValues.inputBackgroundColor && themeValues.inputBackgroundColor};
  color: ${({ themeValues }) => themeValues.color && themeValues.color};
  box-shadow: none;
  resize: ${({ resize }) => resize || "vertical"};
  transition: background 0.3s ease;

  &:focus {
    outline: 3px solid ${ROYAL_BLUE};
    outline-offset: 2px;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      color: #6e727e;
      background-color: #f7f7f7;
    `}

  ${({ $extraStyles }) =>
    css`
      ${$extraStyles}
    `}
`;

const FormTextarea = ({
  ariaLabelledBy = undefined,
  labelDisplayOverride = null,
  labelTextWhenNoError = "",
  errorMessages,
  helperModal = false,
  field,
  fieldActions,
  showErrors,
  themeValues,
  customHeight,
  extraStyles,
  removeFromValue, // regex of characters to remove before setting value
  dataQa = null,
  isRequired = false,
  errorFieldExtraStyles,
  showFieldErrorRow = true,
  labelTextVariant = "pS",
  errorTextVariant = "pXS",
  resize = "vertical", // none, horizontal, vertical, both
  rows = 5,
  cols,
  placeholder,
  maxLength,
  ...props
}) => {
  const setValue = value => {
    if (removeFromValue !== undefined) {
      return fieldActions.set(value.replace(removeFromValue, ""));
    }
    return fieldActions.set(value);
  };

  return (
    <Stack childGap="0.25rem">
      <Box padding="0">
        {helperModal ? (
          <Cluster justify="space-between" align="center">
            {labelDisplayOverride ? (
              labelDisplayOverride
            ) : (
              <Text
                as="label"
                color={themeValues.labelColor}
                variant={labelTextVariant}
                weight={themeValues.fontWeight}
                extraStyles={`word-break: break-word;
                  font-family: Public Sans;
                  &::first-letter {
                    text-transform: uppercase;
                  }`}
                id={createIdFromString(labelTextWhenNoError)}
              >
                {labelTextWhenNoError}
              </Text>
            )}
            {helperModal()}
          </Cluster>
        ) : (
          <Box padding="0" minWidth="100%">
            <Cluster justify="space-between" align="center">
              {labelDisplayOverride ? (
                labelDisplayOverride
              ) : (
                <Text
                  as="label"
                  color={themeValues.labelColor}
                  variant={labelTextVariant}
                  fontWeight={themeValues.fontWeight}
                  extraStyles={`word-break: break-word;
                    font-family: Public Sans;
                    &::first-letter {
                      text-transform: uppercase;
                    }`}
                  id={createIdFromString(labelTextWhenNoError)}
                >
                  {labelTextWhenNoError}
                </Text>
              )}
            </Cluster>
          </Box>
        )}
      </Box>
      <Box padding="0">
        <TextareaField
          aria-labelledby={
            ariaLabelledBy === undefined
              ? createIdFromString(labelTextWhenNoError)
              : ariaLabelledBy
          }
          aria-describedby={createIdFromString(
            labelTextWhenNoError,
            "error message"
          )}
          aria-invalid={
            (field.dirty && field.hasErrors) || (field.hasErrors && showErrors)
          }
          onChange={e => setValue(e.target.value)}
          value={field.rawValue}
          field={field}
          showErrors={showErrors}
          themeValues={themeValues}
          $customHeight={customHeight}
          $extraStyles={extraStyles}
          data-qa={dataQa || labelTextWhenNoError}
          required={isRequired}
          resize={resize}
          rows={rows}
          cols={cols}
          placeholder={placeholder}
          maxLength={maxLength}
          {...props}
        />
      </Box>
      {showFieldErrorRow && (
        <Stack
          direction="row"
          justify="space-between"
          aria-live="polite"
          aria-atomic={true}
        >
          {(field.hasErrors && field.dirty) ||
          (field.hasErrors && showErrors) ? (
            <Text
              color={ERROR_COLOR}
              variant={errorTextVariant}
              weight={themeValues.fontWeight}
              extraStyles={`word-break: break-word;
              font-family: Public Sans;
              &::first-letter {
                text-transform: uppercase;
              }
              ${errorFieldExtraStyles};`}
              id={createIdFromString(labelTextWhenNoError, "error message")}
            >
              {errorMessages[field.errors[0]]}
            </Text>
          ) : (
            <Text
              extraStyles={`height: ${themeValues.lineHeight}; ${errorFieldExtraStyles};`}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default themeComponent(
  FormTextarea,
  "FormTextarea",
  fallbackValues,
  "default"
);
